import { useEffect, useRef, useState, type ReactNode } from "react";
import { Button } from "@legends/ui";
import { fetchMyTickets } from "./api";
import { gaEvent } from "./ga";

// Post-checkout return UX (ADR 0048). Stripe redirects the buyer back to the
// storefront with ?purchase=success|cancelled&tt=<ticketTypeId>. Because the
// webhook — not this redirect — grants the ticket, a "success" return may arrive
// a beat before the entitlement lands, so we resolve one of three real outcomes:
//
//   success    — the entitlement is present (or an immediate dev/free grant)
//   finalizing — success returned, entitlement not yet granted; we poll for it
//   timeout    — polled the bounded window and it still hasn't landed
//   cancelled  — the buyer abandoned checkout (never charged); offer Retry
//
// The hook + banner are shared by every storefront (cave public + catalog) so
// the experience is identical wherever a Fan buys.

/** The three outcomes a Fan can return to, plus the intermediate poll state. */
export type CheckoutStatus = "finalizing" | "success" | "timeout" | "cancelled";

// CROSS-CLIENT CONTRACT (LSC-319/414): the iOS + Android apps MIRROR this poll window on their
// own /app/checkout/return handler — the "Open in the Legends app" link (mobile, any
// non-cancelled outcome) can open the app BEFORE the async webhook grant lands, so both surfaces
// poll /me/tickets for the entitlement and must give up at the same moment (else one looks broken
// beside the other). If you change these, tell the mobile agents — the apps pin 6 × 2s == 12s.
const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 6; // ~12s bounded wait for the asynchronous webhook grant

/**
 * The storefront path the buy is initiated from, handed to the checkout so
 * Stripe returns the buyer to the exact cave/legend page — not the generic root.
 * Query + hash are dropped (the server re-adds its own ?purchase= outcome params).
 */
export function currentReturnPath(): string {
  return window.location.pathname || "/";
}

/**
 * Read (and strip) the ?sso_buy=<ticketTypeId> the OAuth callback echoes back
 * when a Fan was signed in mid-purchase (ADR 0103). The storefront calls this
 * once it knows the Fan is signed in and, if a ticket id comes back, resumes
 * checkout for it. The param is stripped from the URL so a refresh never replays
 * the purchase; the value is an opaque id the server already sanitized and the
 * storefront re-validates against its real catalog before charging.
 */
export function consumeBuyIntent(): string | null {
  const params = new URLSearchParams(window.location.search);
  const buy = params.get("sso_buy");
  if (!buy) return null;
  params.delete("sso_buy");
  const qs = params.toString();
  window.history.replaceState(
    {},
    "",
    window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash,
  );
  return buy;
}

export interface CheckoutReturn {
  /** The current banner state (null = nothing to show). */
  status: CheckoutStatus | null;
  /** The ticket type the return concerns (from ?tt=), for Retry / naming. */
  ticketTypeId: string | null;
  /** Drive the success state directly for an immediate (dev/free) local grant. */
  markGranted: (ticketTypeId: string) => void;
  /** Clear the banner. */
  dismiss: () => void;
}

/**
 * Handle the Stripe return. On mount it reads ?purchase= (and strips it from the
 * URL so a refresh never replays the outcome). A cancel resolves immediately; a
 * success polls GET /me/tickets for a bounded window — pushing refreshed
 * holdings up via `onOwned` and resolving to "success" the instant the ticket
 * lands, else a graceful "timeout" fallback that points the Fan at their tickets.
 */
export function useCheckoutReturn(onOwned: (owned: Set<string>) => void): CheckoutReturn {
  const [status, setStatus] = useState<CheckoutStatus | null>(null);
  const [ticketTypeId, setTicketTypeId] = useState<string | null>(null);
  const onOwnedRef = useRef(onOwned);
  onOwnedRef.current = onOwned;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const purchase = params.get("purchase");
    if (!purchase) return;
    const tt = params.get("tt");

    // Strip the return params up front so a refresh doesn't re-trigger this.
    params.delete("purchase");
    params.delete("session_id");
    params.delete("tt");
    const qs = params.toString();
    window.history.replaceState(
      {},
      "",
      window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash,
    );
    setTicketTypeId(tt);

    if (purchase !== "success") {
      // Cancelled / abandoned — never charged, no phantom entitlement.
      setStatus("cancelled");
      return;
    }

    setStatus("finalizing");
    let done = false;
    let tries = 0;
    let timer = 0;
    const settle = (s: CheckoutStatus) => {
      if (done) return;
      done = true;
      window.clearInterval(timer);
      setStatus(s);
      // GA4 conversion (ADR 0224): the entitlement actually landed on this
      // Stripe return. Fire once, ids only (no PII). No-op unless GA configured.
      if (s === "success") gaEvent("purchase", tt ? { product_id: tt } : {});
    };
    const check = async () => {
      try {
        const held = await fetchMyTickets();
        if (done) return;
        const owned = new Set(held.map((h) => h.ticketTypeId));
        onOwnedRef.current(owned);
        // No tt to confirm (legacy link) → treat the return as success once we
        // have a fresh holdings read.
        if (!tt || owned.has(tt)) settle("success");
      } catch {
        /* transient — keep polling until the bound */
      }
    };
    void check();
    timer = window.setInterval(() => {
      tries += 1;
      if (tries >= MAX_POLLS) {
        settle("timeout");
        return;
      }
      void check();
    }, POLL_INTERVAL_MS);
    return () => {
      done = true;
      window.clearInterval(timer);
    };
  }, []);

  return {
    status,
    ticketTypeId,
    markGranted: (id: string) => {
      setTicketTypeId(id);
      setStatus("success");
      // GA4 conversion (ADR 0224): an immediate dev/free grant is a purchase too.
      gaEvent("purchase", id ? { product_id: id } : {});
    },
    dismiss: () => setStatus(null),
  };
}

export interface CheckoutReturnBannerProps {
  status: CheckoutStatus | null;
  /** Optional ticket name to personalize the copy. */
  ticketName?: string;
  /** Cancelled: re-initiate checkout for the same ticket. */
  onRetry?: () => void;
  /** True while a retry is in flight (disables the Retry button). */
  retrying?: boolean;
  /** Success: an optional primary next action (e.g. Watch now / View my ticket). */
  successAction?: { label: string; onClick: () => void };
}

// A Stripe return is a server-side redirect, which iOS/Android do NOT auto-open into the
// app (a universal link / App Link fires only on a user gesture — LSC-414). So on a mobile
// browser we offer an explicit tap to /app/checkout/return — the path the fan app verifies
// on every host (LSC-324: nginx serves the AASA + assetlinks) — which opens the app (or the
// static return.html fallback if it isn't installed). This is what makes LSC-319's
// auto-close reachable: the claim existed, but nothing ever navigated to it.
function isMobileBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

/** The shared, accessible return banner. role="status" so it's announced. */
export function CheckoutReturnBanner({
  status,
  ticketName,
  onRetry,
  retrying,
  successAction,
}: CheckoutReturnBannerProps) {
  if (!status) return null;

  const box = (tone: "accent" | "warn", children: ReactNode) => (
    <div
      role="status"
      style={{
        marginTop: 14,
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
        padding: "10px 12px",
        borderRadius: "var(--radius)",
        border: `1px solid ${tone === "warn" ? "var(--alert-edge)" : "var(--border-strong)"}`,
        background: tone === "warn" ? "var(--alert-tint)" : "var(--accent-tint)",
        color: tone === "warn" ? "var(--alert-2)" : "var(--accent-hi)",
        fontSize: "var(--fs-xs)",
      }}
    >
      {children}
    </div>
  );

  const named = ticketName ? ` — ${ticketName}` : "";

  // On a mobile browser, offer a tap to the app-verified return path for any non-cancelled
  // outcome. It matters MOST in finalizing/timeout: THIS browser can't confirm the entitlement
  // (webhook lag, or signed out in Safari — /me/tickets 401s and we settle to timeout) but the
  // app can under its own session, so the tap is the recovery, not just a nicety on success
  // (LSC-319/414). A server-side redirect can't auto-open the app; a user gesture on
  // /app/checkout/return does.
  const appReturnLink = isMobileBrowser() ? (
    <a
      href="/app/checkout/return?purchase=success"
      style={{
        marginLeft: "auto",
        color: "var(--accent-hi)",
        textDecoration: "underline",
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      Open in the Legends app
    </a>
  ) : null;

  if (status === "cancelled") {
    return box(
      "warn",
      <>
        <span>Checkout canceled — you were not charged.</span>
        {onRetry && (
          <Button
            size="sm"
            variant="accent"
            onClick={onRetry}
            disabled={retrying}
            style={{ marginLeft: "auto" }}
          >
            {retrying ? "Retrying…" : "Retry"}
          </Button>
        )}
      </>,
    );
  }
  if (status === "finalizing") {
    return box(
      "accent",
      <>
        <span>Finalizing your purchase{named}… this only takes a moment.</span>
        {appReturnLink}
      </>,
    );
  }
  if (status === "timeout") {
    return box(
      "accent",
      <>
        <span>
          Still finalizing{named} — this is taking longer than expected. Your ticket will appear
          below shortly; no need to buy again.
        </span>
        {appReturnLink}
      </>,
    );
  }
  // success
  return box(
    "accent",
    <>
      <span>You&apos;re in{named} — your ticket is ready.</span>
      {successAction && (
        <Button size="sm" variant="accent" onClick={successAction.onClick} style={{ marginLeft: "auto" }}>
          {successAction.label}
        </Button>
      )}
      {appReturnLink}
    </>,
  );
}
